import React, { useEffect, useRef, useState } from "react";
import Layout from "../../Components/Layout";
import DashboardFeed from "../Components/DashboardFeed/DashboardFeed";
import "../DemoStyles/index.scss";
import "./index.scss";
import JourneyToApprentice from "../Components/JourneyToApprentice/JourneyToApprentice";
import ProjectRisk from "../Components/ProjectRisk";
import DashboardJourneyRatio from "../Components/DashboardJourneyRatio";
import DashboardHeatMap from "../Components/DashboardHeatMap";
import { useWindowSize } from "../../../utils/customHooks/useWindowSize";
import { getApi } from "../../../utils/services";
import { ACTIVE_PROJECTS_DROPDOWN, DASHBOARD_FEED, UNDERPAYMENT_FEED_SUMMARY,GET_PREVAILING_WAGE_UNDER_PAYMENT_DETAILS } from "../../../utils/services/apiPath";
import ProjectDetailModal from "../Components/ProjectDetailModal/ProjectDetailModal";
import UnderPaymentModal from "../Components/UnderPaymentFeedDetail/UnderPaymentModal";
import { Autocomplete, TextField, Typography, Backdrop, CircularProgress } from "@mui/material";
import { errorToast } from "../../../utils/toastHelper";
import { createODataQuery } from "../../../utils/services/oData";
import dayjs from "dayjs";
import { useFeatureFlagEnabled } from "posthog-js/react";

const UNDERPAYMENT_EVENT_CATEGORY_ID = 8;

const isUnderpaymentFeedItem = feedItem => feedItem?.eventCategoryTypeId === UNDERPAYMENT_EVENT_CATEGORY_ID;

const mapWorkAreaCalculation = (item, index) => {

  return {
    id: index,
    workAreaName: item.workAreaName,
    laborClassification: item.laborClassification,
    st: item.straightTimeHours,
    ot: item.overTimeHours,
    dt: item.doubleTimeHours,
    backPayment: item.backPayment,
    calculation: {
      wage: {
        tiers: item.wageBreakdown?.tiers ?? [],
        wageRequired: item.wageBreakdown?.wageRequired ?? 0,
        grossAmountEarned: item.wageBreakdown?.grossAmountEarned ?? 0,
        wageBackPayment: item.wageBreakdown?.wageBackPayment ?? 0
      },
      fringe: {
        tiers: item.fringeBreakdown?.tiers ?? [],
        fringeRequired: item.fringeBreakdown?.fringeRequired ?? 0,
        totalFringeBenefitCredit: item.fringeBreakdown?.totalFringeBenefitCredit ?? 0,
        paymentInLieuOfFringeBenefits: item.fringeBreakdown?.paymentInLieuOfFringeBenefits ?? 0,
        fringeReportedOnPayroll: item.fringeBreakdown?.fringeReportedOnPayroll ?? 0,
        fringeBackPayment: item.fringeBreakdown?.fringeBackPayment ?? 0
      }
    }
  };
};

const mapToUnderpaymentRecord = response => ({
  employeeName: response?.employeeName,
  weekEnding: response?.weekEndingDate ? dayjs(response.weekEndingDate).format("MM/DD/YYYY") : "",
  project: response?.projectName,
  laborClassification: response?.laborClassification,
  grossAmount: response?.grossAmount,
  totalBackPayment: response?.totalBackPayment,
  workAreas: (response?.workAreaCalculations ?? []).map(mapWorkAreaCalculation)
});

export default function DemoDashboard() {
  const [dashboardFeedData, setDashboardFeedData] = useState();
  const [hideHeatMap, setHideHeatMap] = useState(false);
  const [journeyWrapperHeight, setJourneyWrapperHeight] = useState(261);
  const { width, height } = useWindowSize(true);
  const journeyWrapperRef = useRef(null);
  const [open, setOpen] = React.useState(false);
  const [feedData, setFeedData] = React.useState(null);
  const [underpaymentSummary, setUnderpaymentSummary] = React.useState(null);
  const [selectedUnderpaymentRecord, setSelectedUnderpaymentRecord] = React.useState(null);
  const [activeProjects, setActiveProjects] = useState([])
  const [selectedProjects, setSelectedProjects] = useState([])
  const [selectedProjectsODataQuery, setSelectedProjectsODataQuery] = useState('')
  const [selectedProjectsQuery, setSelectedProjectsQuery] = useState('')

  const isDetailedUnderpaymentFeedEnabled = useFeatureFlagEnabled("EnableDetailedUnderPaymentFeedDetails");
  // Loading state management - tracks which components are currently loading
  const [loadingComponents, setLoadingComponents] = useState(new Set());
  const isLoading = loadingComponents.size > 0;

  // Helper functions to track loading state
  const startLoading = (componentName) => {
    setLoadingComponents(prev => new Set(prev).add(componentName));
  };

  const stopLoading = (componentName) => {
    setLoadingComponents(prev => {
      const newSet = new Set(prev);
      newSet.delete(componentName);
      return newSet;
    });
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleOpenUnderpaymentList = () => {
    if (!isDetailedUnderpaymentFeedEnabled) return;
    setFeedData({ eventCategoryTypeId: UNDERPAYMENT_EVENT_CATEGORY_ID });
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    setFeedData(null);
    setSelectedUnderpaymentRecord(null);
  };

  const getFeedData = async () => {
    startLoading('dashboardFeed');
    try {
      const { error, data } = await getApi(DASHBOARD_FEED + createODataQuery(["projectGuid"], [], selectedProjectsODataQuery, null, null, null, null, false));
      if (!error) setDashboardFeedData(data?.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)) || []);
    } catch (error) {
      errorToast(error?.message || "Unable to fetch dashboard feed data.");
    } finally {
      stopLoading('dashboardFeed');
    }
  };

  const getActiveProjects = async () => {
    startLoading('activeProjects');
    try {
      const res = await getApi(ACTIVE_PROJECTS_DROPDOWN)
      setActiveProjects(res?.filter(p => p.projectId != "All"))
    } catch (err) {
      errorToast("Unable to fetch active projects")
    } finally {
      stopLoading('activeProjects');
    }
  }

  const getUnderpaymentSummary = async () => {
    startLoading('underpaymentSummary');
    try {
      const query = selectedProjectsQuery
        ? `?projectIds=${encodeURIComponent(selectedProjectsQuery)}`
        : '';
      setUnderpaymentSummary(null);
      const response = await getApi(UNDERPAYMENT_FEED_SUMMARY + query);
      const apiError = response?.error;
      const summary = response && typeof response === "object" && "data" in response ? response.data : response;
      setUnderpaymentSummary(apiError ? null : summary);
    } catch (error) {
      errorToast(error?.message || "Unable to fetch underpayment summary.");
    } finally {
      stopLoading('underpaymentSummary');
    }
  };

  const toggleSelectedActiveProjects = (_e, selected) => {
    if (selected.length === 0) {
      setSelectedProjects([])
      setSelectedProjectsQuery('')
      setSelectedProjectsODataQuery('')
      return;
    }

    setSelectedProjects(selected)
    setSelectedProjectsODataQuery(`projectGuid in (${selected.filter(p => p.projectId !== 'All').map((p) => p.projectId).join(",")})`)
    setSelectedProjectsQuery(selected.filter(p => p.projectId !== 'All').map((p) => p.projectId).join(","))
  }

  useEffect(() => {
    if (journeyWrapperRef.current?.offsetHeight) {
      setJourneyWrapperHeight(journeyWrapperRef.current.offsetHeight + 3);
    }
  }, [width, height, journeyWrapperRef]);

  useEffect(() => {
    getActiveProjects();
  }, []);

  useEffect(() => {
    getFeedData();
    if (isDetailedUnderpaymentFeedEnabled) {
      getUnderpaymentSummary();
    } else {
      setUnderpaymentSummary(null);
    }
  }, [selectedProjectsODataQuery, isDetailedUnderpaymentFeedEnabled])

  const showUnderpaymentModal = isDetailedUnderpaymentFeedEnabled && isUnderpaymentFeedItem(feedData);

  const handleSelectUnderpaymentRecord = async row => {
    startLoading('underpaymentDetail');
    try {
      const query = new URLSearchParams({
        projectGuid: row?.projectGuid ?? "",
        employeeGuid: row?.employeeGuid ?? "",
        weekEndingDate: row?.payPeriodEndingDate ?? ""
      }).toString();
      const response = await getApi(`${GET_PREVAILING_WAGE_UNDER_PAYMENT_DETAILS}?${query}`);

      const apiError = response?.error;
      const apiData = response && typeof response === "object" && "data" in response ? response.data : response;

      if (apiError) {
        errorToast(apiError?.message || apiError || "Unable to load underpayment details.");
        return;
      }
      setSelectedUnderpaymentRecord(mapToUnderpaymentRecord(apiData));
    } catch (error) {
      errorToast(error?.message || "Unable to load underpayment details.");
    } finally {
      stopLoading('underpaymentDetail');
    }
  };

  const handleBackToUnderpaymentList = () => setSelectedUnderpaymentRecord(null);

  useEffect(() => {
    if (!open || !showUnderpaymentModal) return;
    setSelectedUnderpaymentRecord(null);
  }, [open, showUnderpaymentModal, feedData]);

  return (
    <Layout>
      <div className="demo-dashboard-wrapper">
        <div className="demo-container">
          <div>
            <div className="dashboard-feed-title">
              Filters
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Autocomplete
                multiple
                id="tags-outlined"
                options={activeProjects}
                getOptionLabel={(option) => option.projectName}
                value={selectedProjects}
                onChange={(e, value) => toggleSelectedActiveProjects(e, value)}
                filterSelectedOptions
                disabled={isLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selected Projects"
                    placeholder="Selected Projects"
                  />
                )}
              />
            </div>
          </div>
          <hr />
          <div className="demo-dashboard">
            <div className="demo-dashboard-left">
              <DashboardFeed feedJson={dashboardFeedData} underpaymentSummary={underpaymentSummary} handleOpen={handleOpen} setFeedData={setFeedData} onOpenUnderpaymentPreview={handleOpenUnderpaymentList} />
            </div>
            <div className="demo-dashboard-right">
              <JourneyToApprentice journeyWrapperRef={journeyWrapperRef} selectedProjectsQuery={selectedProjectsQuery} startLoading={startLoading} stopLoading={stopLoading} />
              <div className="demo-dashboard-bottom" style={{ minHeight: `calc(100% - ${journeyWrapperHeight}px)` }}>
                <div className={`demo-dashboard-bottom-left ${hideHeatMap ? "with-hidden-heatmap" : ""}`}>
                  <DashboardJourneyRatio selectedProjectsQuery={selectedProjectsQuery} startLoading={startLoading} stopLoading={stopLoading} />
                  <ProjectRisk selectedProjectsQuery={selectedProjectsQuery} startLoading={startLoading} stopLoading={stopLoading} />
                </div>
                <div className={`demo-dashboard-bottom-right ${hideHeatMap ? 'hidden' : ''}`}>
                  <DashboardHeatMap setHideHeatMap={setHideHeatMap} selectedProjectsQuery={selectedProjectsQuery} startLoading={startLoading} stopLoading={stopLoading} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showUnderpaymentModal ? (
        <UnderPaymentModal
          open={open}
          setOpen={handleModalClose}
          selectedRecord={selectedUnderpaymentRecord}
          onSelectRecord={handleSelectUnderpaymentRecord}
          onBackToList={handleBackToUnderpaymentList}
          projectIds={selectedProjectsQuery}
        />
      ) : (
        <ProjectDetailModal feedData={feedData} open={open} setOpen={handleModalClose} />
      )}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: "absolute"
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Layout>
  );
}